import { useCallback, useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import ConfirmationWrapper from "../common/ConfirmationWrapper";
import Loading from "../common/Loading";
import NoContent from "../common/NoContent";
import ProgressiveControl from "../common/ProgressiveControl";
import { useAppContext } from "../../providers/ProviderAppContainer";
import { TEXT_NORMAL, TEXT_SMALL } from "../../style";
import FieldNote from "./common/FieldNote";
import ManagedListFrame from "./common/ManagedListFrame";
import WebsiteMediaInput from "./WebsiteMediaInput";

const REVIEWER_OPTIONS = [
    { label: "Student", value: "Student" },
    { label: "Parent", value: "Parent" },
];

export default function WebsiteTestimonials({ bare = false, title = "Reviews" } = {}) {
    const { requestAPI, showToast } = useAppContext();
    const [loading, setLoading] = useState();
    const [error, setError] = useState();
    const [rows, setRows] = useState();
    const [courses, setCourses] = useState([]);
    const [dialog, setDialog] = useState({ visible: false });

    const loadRows = useCallback(() => {
        requestAPI({
            requestPath: "website/testimonials",
            requestMethod: "GET",
            setLoading,
            onRequestStart: setError,
            onRequestFailure: setError,
            onResponseReceieved: (data, responseCode) => {
                if (data && responseCode === 200) setRows(data);
                else setError("Couldn't load Testimonials");
            },
        });
    }, [requestAPI]);

    useEffect(() => {
        loadRows();
        requestAPI({
            requestPath: "website/courses",
            requestMethod: "GET",
            onResponseReceieved: (data, responseCode) => {
                if (data && responseCode === 200) setCourses(data);
            },
        });
    }, [loadRows, requestAPI]);

    return (
        <ManagedListFrame bare={bare} title={title} highlights={["Each review is one card in the home page slider", "The face photo is the small circle. A review photo or video opens from the button on the card"]} onAdd={() => setDialog({ visible: true, record: { is_published: true, media_type: "image" } })}>
            {loading ? <Loading /> : error ? <NoContent error={error} /> : (
                <div className={bare ? "flex flex-column gap-2" : "flex-1 overflow-y-scroll flex flex-column gap-2 p-2"}>
                    {rows?.length ? rows.map((row) => (
                        <Row key={row.id} row={row} setRows={setRows} requestAPI={requestAPI} showToast={showToast} openEdit={(record) => setDialog({ visible: true, record })} />
                    )) : <NoContent />}
                </div>
            )}
            {dialog.visible ? <Editor dialog={dialog} courses={courses} setDialog={setDialog} setRows={setRows} requestAPI={requestAPI} showToast={showToast} /> : null}
        </ManagedListFrame>
    );
}

function Row({ row, setRows, requestAPI, showToast, openEdit }) {
    const [deleting, setDeleting] = useState();
    const remove = () => {
        requestAPI({
            requestPath: `website/testimonials/${row.id}`,
            requestMethod: "DELETE",
            setLoading: setDeleting,
            parseResponseBody: false,
            onResponseReceieved: (_, responseCode) => {
                if (responseCode === 204) {
                    showToast({ severity: "success", summary: "Deleted", detail: "Review removed", life: 1000 });
                    setRows((prev) => prev?.filter((item) => item.id !== row.id));
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: "Failed To Delete !", life: 2000 });
                }
            },
        });
    };
    const active = row.is_published !== false && row.is_published !== 0;
    const meta = [row.reviewer_type, row.course || row.role].filter(Boolean).join(" � ");
    return (
        <div className={`flex gap-3 align-items-center border-round py-2 px-3 text-white ${active ? "bg-green-500" : "bg-red-500"}`}>
            <div className="flex flex-column flex-1 overflow-hidden">
                <span className={`${TEXT_NORMAL} font-semibold`}>{row.name || "Untitled"}</span>
                {meta ? <span className={TEXT_SMALL}>{meta}</span> : null}
            </div>
            <i className="pi pi-pencil cursor-pointer" onClick={() => openEdit(row)} />
            <ProgressiveControl loading={deleting} control={<ConfirmationWrapper action={remove}><i className="pi pi-trash cursor-pointer" /></ConfirmationWrapper>} />
        </div>
    );
}

function namedOptions(values, current) {
    const options = [];
    const seen = new Set();
    values.forEach((value) => {
        const label = String(value || "").trim();
        if (!label || seen.has(label)) return;
        seen.add(label);
        options.push({ label, value: label });
    });
    if (current && !seen.has(current)) options.unshift({ label: current, value: current });
    return options;
}

function Editor({ dialog, courses, setDialog, setRows, requestAPI, showToast }) {
    const [record, setRecord] = useState({ is_published: true, media_type: "image", ...(dialog.record || {}) });
    const [saving, setSaving] = useState();
    const isNew = !record.id;
    const reviewerOptions = namedOptions(REVIEWER_OPTIONS.map((item) => item.value), record.reviewer_type);
    const courseOptions = namedOptions((courses || []).map((course) => course.title), record.course);
    const save = () => {
        if (!String(record.name || "").trim()) {
            showToast({ severity: "error", summary: "Name required", detail: "Enter the person's name", life: 2000 });
            return;
        }
        const rating = String(record.rating ?? "").trim();
        requestAPI({
            requestPath: "website/testimonials",
            requestMethod: isNew ? "POST" : "PATCH",
            requestPostBody: { ...record, rating: rating === "" ? null : rating, media_type: record.media_type || "image" },
            setLoading: setSaving,
            onRequestFailure: () => showToast({ severity: "error", summary: "Failed", detail: "Failed To Save !", life: 2000 }),
            onResponseReceieved: (saved, responseCode) => {
                if (saved && (responseCode === 200 || responseCode === 201)) {
                    setRows((prev) => (isNew ? [...(prev || []), saved] : prev.map((item) => (item.id === saved.id ? saved : item))));
                    showToast({ severity: "success", summary: "Saved", detail: "Review saved", life: 1000 });
                    setDialog({ visible: false });
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: saved?.error || "Failed To Save !", life: 2000 });
                }
            },
        });
    };
    return (
        <Dialog header={isNew ? "Add a review" : "Edit review"} visible className="w-11" onHide={() => setDialog({ visible: false })}>
            <div className="flex flex-column">
                    <FloatLabel className="mt-5">
                        <InputText id="name" className="w-full" value={record?.name ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, name: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="name">Person's name</label>
                    </FloatLabel>
                    <FieldNote text="Shown under the review." />
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="title" className="w-full" value={record?.title ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, title: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="title">Review heading</label>
                        </FloatLabel>
                        <FieldNote text="Optional bold line above the quote." />
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputTextarea id="description" className="w-full" rows={4} autoResize value={record?.description || ""} onChange={(event) => setRecord((prev) => ({ ...prev, description: event.target.value }))} disabled={saving} />
                            <label htmlFor="description">Review text</label>
                        </FloatLabel>
                        <FieldNote text="The quote on the card." />
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <Dropdown inputId="reviewer_type" className="w-full" options={reviewerOptions} optionLabel="label" optionValue="value" value={record?.reviewer_type || null} onChange={(event) => setRecord((prev) => ({ ...prev, reviewer_type: event.value || "" }))} showClear filter disabled={saving} />
                            <label htmlFor="reviewer_type">Student or parent</label>
                        </FloatLabel>
                        <FieldNote text="Shown under the name. Example: Student or Parent." />
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <Dropdown inputId="course" className="w-full" options={courseOptions} optionLabel="label" optionValue="value" value={record?.course || null} onChange={(event) => setRecord((prev) => ({ ...prev, course: event.value || "" }))} showClear filter disabled={saving} />
                            <label htmlFor="course">Course</label>
                        </FloatLabel>
                        <FieldNote text="Shown after Student or Parent. The list comes from the shared Courses page." />
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="role" className="w-full" value={record?.role ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, role: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="role">Role line</label>
                        </FloatLabel>
                        <FieldNote text="Used only when no course is selected. Example: CA Foundation." />
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="score" className="w-full" value={record?.score ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, score: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="score">Result line</label>
                        </FloatLabel>
                        <FieldNote text="Example: 98% Score. Leave empty to hide it." />
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="rating" className="w-full" value={record?.rating ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, rating: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="rating">Score out of 10</label>
                        </FloatLabel>
                        <FieldNote text="Example: 9. The card shows 9 / 10 and stars. A score of 5 or less is doubled, so enter 8, 9, or 10 for a high score." />
                    </div>
                    <WebsiteMediaInput label="Face photo" field="image" hint="Required size: 96 � 96 px (square). A larger photo is scaled to fit this small circle. The whole photo stays visible." record={record} setRecord={setRecord} disabled={saving} />
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="image_alt" className="w-full" value={record?.image_alt ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, image_alt: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="image_alt">Face photo description</label>
                        </FloatLabel>
                        <FieldNote text="Read by screen readers. Use the person's name." />
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <Dropdown inputId="media_type" className="w-full" options={[{ label: "Photo", value: "image" }, { label: "Video", value: "video" }]} optionLabel="label" optionValue="value" value={record?.media_type === "video" ? "video" : "image"} onChange={(event) => setRecord((prev) => ({ ...prev, media_type: event.value }))} disabled={saving} />
                            <label htmlFor="media_type">Review photo or video</label>
                        </FloatLabel>
                        <FieldNote text="Photo shows a View Photo button. Video shows Watch Review." />
                    </div>
                    <WebsiteMediaInput label={record?.media_type === "video" ? "Review video" : "Review photo"} type={record?.media_type === "video" ? "video" : "image"} field="media_url" hint={record?.media_type === "video" ? "Optional. This video opens when someone taps Watch Review." : "Optional. Required size: 1280 � 720 px (16:9). A larger photo is scaled to fit when someone taps View Photo. The whole photo stays visible."} record={record} setRecord={setRecord} disabled={saving} />
                    <div className="flex align-items-center gap-2 mt-4">
                        <Checkbox inputId="is_published" checked={record?.is_published !== false && record?.is_published !== 0} onChange={(event) => setRecord((prev) => ({ ...prev, is_published: event.checked }))} disabled={saving} />
                        <label htmlFor="is_published" className={TEXT_SMALL}>Show this review on the website</label>
                    </div>
                <Button className="mt-5 align-self-start" label="Save" icon="pi pi-check" loading={saving} onClick={save} />
            </div>
        </Dialog>
    );
}
